import { EditableAddress } from '@/components/address/address-manager'
import {
    Address,
    AddressOwner,
    createAddress,
    deleteAddress,
    getAddresses,
    updateAddress,
} from './addresses-api'

/** Campos que viajam para a API — o resto é estado só do formulário. */
const FIELDS = [
    'label',
    'street',
    'number',
    'complement',
    'neighborhood',
    'city',
    'cityCode',
    'state',
    'zipCode',
    'country',
] as const

function payload(address: EditableAddress) {
    const data: Record<string, unknown> = {}
    for (const field of FIELDS) data[field] = address[field] ?? null
    data.isPrimary = !!address.isPrimary
    data.sortOrder = address.sortOrder ?? 0
    return data
}

function changed(local: EditableAddress, remote: Address): boolean {
    if (!!local.isPrimary !== remote.isPrimary) return true
    return FIELDS.some(
        field => (local[field] ?? null) !== ((remote as any)[field] ?? null)
    )
}

/**
 * Persiste a lista de endereços editada no formulário.
 *
 * O `AddressManager` é controlado e não grava nada — quem grava é o formulário,
 * ao submeter. Isso mantém criação e edição com o mesmo comportamento (adicionar
 * um endereço e cancelar o cadastro não deixa registro solto) e concentra aqui a
 * decisão do que é criação, alteração e remoção.
 *
 * Envia só o que mudou: um PATCH por endereço tocado, em vez de reescrever a
 * lista inteira a cada salvamento — cada escrita invalida o cache do dono e
 * aparece no histórico da entidade.
 *
 * Erros não são engolidos: a entidade já foi salva quando isto roda, então o
 * caller precisa saber que o endereço não acompanhou.
 */
export async function syncAddresses(
    owner: AddressOwner,
    addresses: EditableAddress[]
): Promise<void> {
    const current = await getAddresses(owner)
    const remoteById = new Map<number, Address>(
        current.success ? current.result.map(a => [a.id, a]) : []
    )

    const operations: Promise<unknown>[] = []

    for (const address of addresses) {
        if (address._deleted) {
            if (address.id) operations.push(deleteAddress(address.id))
            continue
        }

        if (!address.id) {
            operations.push(createAddress({ ...owner, ...payload(address) }))
            continue
        }

        const remote = remoteById.get(address.id)
        if (!remote || changed(address, remote)) {
            operations.push(updateAddress(address.id, payload(address)))
        }
    }

    await Promise.all(operations)
}

/** Converte a resposta da API no formato que o formulário edita. */
export function toEditable(addresses: Address[]): EditableAddress[] {
    return addresses.map(address => ({
        id: address.id,
        label: address.label,
        street: address.street,
        number: address.number,
        complement: address.complement,
        neighborhood: address.neighborhood,
        city: address.city,
        cityCode: address.cityCode,
        state: address.state,
        zipCode: address.zipCode,
        country: address.country,
        isPrimary: address.isPrimary,
        sortOrder: address.sortOrder,
    }))
}
